import { useEffect, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { ReactComponent as RightArrowIcon } from '../../../../../../assets/icons/arrow-right.svg';
import CustomButton from '../../../../../../shared/components/Buttons/CustomButton/CustomButton';
import DropdownMenu from '../../../../../../shared/components/Globals/DropdownMenu/DropdownMenu';
import Tooltip from '../../../../../../shared/components/Globals/Tooltip/Tooltip';
import ActivationMissingInfoModal from '../../../../../../shared/components/Modals/ActivationMissingInfoModal/ActivationMissingInfoModal';
import {
  CLICK_ACTION,
  COMPLETE_ACTIVATION_NOW,
  MOVE_TO_ACTIVATION,
  SAVE_AS_PENDING_ACTIVATION,
  SOURCE_PROFILE
} from '../../../../../../shared/constants/tagManager';
import { useBroker } from '../../../../../../shared/hooks/useBroker/useBroker';
import type { IProspect } from '../../../../../../shared/interfaces/prospects';
import type { IQuote } from '../../../../../../shared/interfaces/quotes';
import TagManagerService from '../../../../../../shared/services/gtm/tagManagerService';
import { TRANSLATIONS_PROSPECTS } from '../../../../../../shared/translations/prospects';
import { ProspectStatus } from '../../../../../../shared/types/prospects';
import { getActivationQuotes, isReadyToActivate } from '../../../../../../shared/utils/checks/activationChecks';

import './ProfileActions.scss';

interface IProfileActionsProps {
  prospect: IProspect;
  quotes?: IQuote[];
  onGetQuote: () => void;
  onActivate?: (id: number) => void;
  onRefreshProspect?: () => void;
  onMarkLost?: () => void;
  onMarkInactive?: () => void;
  disabled?: boolean;
}

const ProfileActions = ({
  prospect,
  quotes = [],
  onGetQuote,
  onActivate,
  onRefreshProspect,
  onMarkLost,
  onMarkInactive,
  disabled = false
}: IProfileActionsProps) => {
  const { t } = useTranslation();
  const { addonTypeById } = useBroker();
  const [showMore, setShowMore] = useState(false);
  const [showActivationModal, setShowActivationModal] = useState(false);
  const moreRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!showMore) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (moreRef.current && !moreRef.current.contains(event.target as Node)) {
        setShowMore(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showMore]);

  const hasAccepted = getActivationQuotes(quotes).length > 0;
  const canActivate = isReadyToActivate(prospect, quotes, addonTypeById);

  const moreMenuItems = [
    {
      key: 'lost',
      label: t(TRANSLATIONS_PROSPECTS.profile.actions.markLost),
      onClick: () => {
        onMarkLost?.();
        setShowMore(false);
      }
    },
    {
      key: 'inactive',
      label: t(TRANSLATIONS_PROSPECTS.profile.actions.markInactive),
      onClick: () => {
        onMarkInactive?.();
        setShowMore(false);
      }
    }
  ];

  const openActivationModal = () => {
    TagManagerService.pushEvent(CLICK_ACTION, MOVE_TO_ACTIVATION, SOURCE_PROFILE);
    setShowActivationModal(true);
  };

  const closeActivationModal = () => setShowActivationModal(false);

  const handleSaveAsPending = () => {
    TagManagerService.pushEvent(CLICK_ACTION, SAVE_AS_PENDING_ACTIVATION);
    closeActivationModal();
    onRefreshProspect?.();
  };

  const handleCompleteNow = () => {
    TagManagerService.pushEvent(CLICK_ACTION, COMPLETE_ACTIVATION_NOW);
    closeActivationModal();
    onRefreshProspect?.();
    document.querySelector('.client-data-card')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const renderPrimary = () => {
    if (prospect.status === ProspectStatus.PendingQuote) {
      return (
        <CustomButton variant="primary" size="m" onClick={onGetQuote}>
          {t(TRANSLATIONS_PROSPECTS.profile.actions.getQuote)}
          <RightArrowIcon className="profile-actions__arrow" />
        </CustomButton>
      );
    }
    if (prospect.status === ProspectStatus.Quoted) {
      return (
        <Tooltip
          content={t(TRANSLATIONS_PROSPECTS.detail.quoted.acceptTooltip)}
          disabled={hasAccepted}
          triggerClassName="profile-actions__tooltip"
        >
          <CustomButton
            variant={hasAccepted ? 'primary' : 'default'}
            size="m"
            disabled={!hasAccepted}
            onClick={openActivationModal}
          >
            {t(TRANSLATIONS_PROSPECTS.detail.quoted.moveToActivation)}
            <RightArrowIcon className="profile-actions__arrow" />
          </CustomButton>
        </Tooltip>
      );
    }
    if (prospect.status === ProspectStatus.PendingActivation) {
      if (canActivate) return null;
      return (
        <Tooltip
          content={t(TRANSLATIONS_PROSPECTS.profile.activation.warningBody)}
          triggerClassName="profile-actions__tooltip"
        >
          <CustomButton variant="default" size="m" disabled onClick={() => onActivate?.(prospect.id)}>
            {t(TRANSLATIONS_PROSPECTS.detail.pendingActivation.activate)}
            <RightArrowIcon className="profile-actions__arrow" />
          </CustomButton>
        </Tooltip>
      );
    }
    return null;
  };

  const primary = renderPrimary();
  if (!primary) return null;

  return (
    <>
      <div className="profile-actions">
        {primary}
        <div className="profile-actions__more" ref={moreRef}>
          <CustomButton
            variant="default"
            size="m"
            onClick={() => setShowMore((value) => !value)}
            aria-label="..."
            aria-haspopup="menu"
            aria-expanded={showMore}
            disabled={disabled}
          >
            ···
          </CustomButton>
          {showMore && <DropdownMenu placement="bottom" items={moreMenuItems} />}
        </div>
      </div>

      <ActivationMissingInfoModal
        open={showActivationModal}
        prospect={prospect}
        quotes={quotes}
        onSaveAsPending={handleSaveAsPending}
        onCompleteNow={handleCompleteNow}
        onClose={closeActivationModal}
      />
    </>
  );
};

export default ProfileActions;
